import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Layout,
  MenuProps,
  message,
  Modal,
  Select,
  Space,
  Tag,
  TimePicker,
  Typography,
} from "antd";
import type { Dayjs } from "dayjs";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import dayjs from "dayjs";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  PlusCircle,
  Upload,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import axiosClient from "../../api/axiosClient";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setCheckoutContext, resetFlow, fetchTaxRate } from "../../store/posSlice";
import { updateBookingStatus, rescheduleBooking } from "../../store/salesSlice";
import { getPermissions } from "../../utils/permissions";

import ImportAppointmentsModal from "./ImportAppointmentsModal";
import "./AdminCalendarPage.css";

const { Content } = Layout;
const { Text } = Typography;

type ViewMode = "day" | "week" | "month";
type AppointmentColor = "pink" | "teal" | "purple" | "blue" | "green";

type Appointment = {
  id: string;
  clientId: string;
  clientName: string;
  service: string;
  staffMember: string;
  startAt: string;
  endAt: string;
  startTime: string;
  endTime: string;
  price: number;
  color: AppointmentColor;
  status: string;
};

type Barber = {
  user_id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
};

type Client = {
  client_id: string;
  name: string;
  email?: string;
  phone?: string;
};

type WaitlistEntry = {
  waitlist_id: string;
  client_name: string;
  client_phone: string;
  service: string;
  created_at: string;
};

type StaffTimeBlockUI = {
  block_id: string;
  staff_id: string;
  block_date: string;
  start_time: string; // HH:MM
  end_time: string;
  reason: string;
};

function parseTimeSlot(time: string): { hour: number; minute: number } {
  const m = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return { hour: 9, minute: 0 };
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const period = m[3].toUpperCase();
  if (period === "PM" && h !== 12) h += 12;
  if (period === "AM" && h === 12) h = 0;
  return { hour: h, minute: min };
}

function slotToISO(date: Date, time: string): string {
  const { hour, minute } = parseTimeSlot(time);
  return dayjs(date).hour(hour).minute(minute).second(0).millisecond(0).toISOString();
}


type RealService = {
  service_id: string;
  name: string;
  price: number;
  duration_min: number;
};

const TIME_SLOTS = [
  "7:00 AM","7:30 AM","8:00 AM","8:30 AM","9:00 AM","9:30 AM",
  "10:00 AM","10:30 AM","11:00 AM","11:30 AM","12:00 PM","12:30 PM",
  "1:00 PM","1:30 PM","2:00 PM","2:30 PM","3:00 PM","3:30 PM",
  "4:00 PM","4:30 PM","5:00 PM","5:30 PM","6:00 PM","6:30 PM",
  "7:00 PM","7:30 PM","8:00 PM",
];

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ── "Now" line (red current-time indicator on Day/Week views) ─────────────────
const NOW_LINE_START_MIN = 7 * 60;      // matches TIME_SLOTS start, 7:00 AM
const NOW_LINE_END_MIN = 20 * 60 + 30;  // matches TIME_SLOTS end, last slot 8:00-8:30 PM
const NOW_LINE_ROW_PX = 80;             // matches .calendar-slot-cell / .calendar-time-cell min-height

function useNowTick() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function nowLineSlot(now: Date): { rowIndex: number; offsetPx: number } | null {
  const mins = now.getHours() * 60 + now.getMinutes();
  if (mins < NOW_LINE_START_MIN || mins > NOW_LINE_END_MIN) return null;
  const sinceStart = mins - NOW_LINE_START_MIN;
  const rowIndex = Math.floor(sinceStart / 30);
  const offsetPx = ((sinceStart % 30) / 30) * NOW_LINE_ROW_PX;
  return { rowIndex, offsetPx };
}

function NowTimeLabel({ now, gridRow }: { now: Date; gridRow: number }) {
  const slot = nowLineSlot(now);
  if (!slot) return null;
  return (
    <div
      className="calendar-now-time-label"
      style={{ gridColumn: 1, gridRow, marginTop: slot.offsetPx }}
    >
      <span>{now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
    </div>
  );
}

function NowTimeBar({ now, gridRow, gridColumn }: { now: Date; gridRow: number; gridColumn: number | string }) {
  const slot = nowLineSlot(now);
  if (!slot) return null;
  return (
    <div
      className="calendar-now-time-bar"
      style={{ gridColumn, gridRow, marginTop: slot.offsetPx }}
    />
  );
}

// ── Date helpers ─────────────────────────────────────────────────────────────

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekDays(date: Date): Date[] {
  const start = getWeekStart(date);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function getMonthCells(date: Date): (Date | null)[] {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = (firstDay.getDay() + 6) % 7;
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatDateLabel(date: Date, mode: ViewMode): string {
  if (mode === "day") {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  }
  if (mode === "week") {
    const start = getWeekStart(date);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const s = start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const e = end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${s} – ${e}`;
  }
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function appointmentColorForService(service: string): AppointmentColor {
  const s = service.toLowerCase();
  if (s.includes("haircut") || s.includes("blowout")) return "pink";
  if (s.includes("manicure") || s.includes("pedicure") || s.includes("wax")) return "teal";
  if (s.includes("color") || s.includes("treatment") || s.includes("highlight")) return "purple";
  return "blue";
}

function appointmentClass(color: AppointmentColor) {
  return `calendar-appointment calendar-appointment-${color}`;
}

// ── Date range helpers for fetching ──────────────────────────────────────────

function getFetchRange(date: Date, mode: ViewMode): { from: string; to: string } {
  if (mode === "day") {
    const from = new Date(date);
    from.setHours(0, 0, 0, 0);
    const to = new Date(date);
    to.setHours(23, 59, 59, 999);
    return { from: from.toISOString(), to: to.toISOString() };
  }
  if (mode === "week") {
    const from = getWeekStart(date);
    const to = new Date(from);
    to.setDate(from.getDate() + 7);
    return { from: from.toISOString(), to: to.toISOString() };
  }
  // month
  const from = new Date(date.getFullYear(), date.getMonth(), 1);
  const to = new Date(date.getFullYear(), date.getMonth() + 1, 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function WeekView({
  weekDays,
  appointments,
  onAppointmentClick,
  onSlotClick,
}: {
  weekDays: Date[];
  appointments: Appointment[];
  onAppointmentClick: (appt: Appointment) => void;
  onSlotClick: (date: Date, time: string) => void;
}) {
  const today = new Date();
  const now = useNowTick();
  const nowSlot = nowLineSlot(now);
  const todayIndex = weekDays.findIndex((d) => isSameDay(d, today));

  function getApptForDayAndSlot(day: Date, time: string): Appointment | undefined {
    return appointments.find(
      (a) => isSameDay(new Date(a.startAt), day) && a.startTime === time
    );
  }

  return (
    <div
      className="calendar-week-grid"
      style={{ gridTemplateColumns: `80px repeat(7, minmax(120px, 1fr))` }}
    >
      {nowSlot && todayIndex !== -1 && (
        <>
          <NowTimeLabel now={now} gridRow={nowSlot.rowIndex + 2} />
          <NowTimeBar now={now} gridRow={nowSlot.rowIndex + 2} gridColumn={todayIndex + 2} />
        </>
      )}

      {/* Header row */}
      <div className="calendar-header-cell" />
      {weekDays.map((day, i) => (
        <div
          key={i}
          className={`calendar-week-day-header${isSameDay(day, today) ? " calendar-week-today" : ""}`}
        >
          <span className="cal-week-day-name">{DAY_NAMES[i]}</span>
          <span className={`cal-week-day-num${isSameDay(day, today) ? " cal-today-num" : ""}`}>
            {day.getDate()}
          </span>
        </div>
      ))}

      {/* Time rows */}
      {TIME_SLOTS.map((time) => (
        <>
          <div key={`${time}-lbl`} className="calendar-time-cell">{time}</div>
          {weekDays.map((day, i) => {
            const appt = getApptForDayAndSlot(day, time);
            return (
              <div
                key={`${i}-${time}`}
                className="calendar-slot-cell"
                onClick={() => !appt && onSlotClick(day, time)}
              >
                {appt && (
                  <div
                    className={appointmentClass(appt.color)}
                    style={{ padding: "6px 8px", borderRadius: 8, cursor: "pointer", height: "100%" }}
                    onClick={(e) => { e.stopPropagation(); onAppointmentClick(appt); }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 12, lineHeight: 1.3 }}>{appt.service}</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>{appt.clientName}</div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      ))}
    </div>
  );
}

function MonthView({
  currentDate,
  appointments,
  onDayClick,
}: {
  currentDate: Date;
  appointments: Appointment[];
  onDayClick: (day: Date) => void;
}) {
  const today = new Date();
  const cells = getMonthCells(currentDate);

  function apptCountForDay(day: Date) {
    return appointments.filter((a) => isSameDay(new Date(a.startAt), day)).length;
  }

  function apptsForDay(day: Date) {
    return appointments.filter((a) => isSameDay(new Date(a.startAt), day));
  }

  return (
    <div className="calendar-month-wrap">
      <div className="calendar-month-header">
        {DAY_NAMES.map((d) => (
          <div key={d} className="calendar-month-day-label">{d}</div>
        ))}
      </div>
      <div className="calendar-month-grid">
        {cells.map((day, idx) => (
          <div
            key={idx}
            className={[
              "calendar-month-cell",
              !day ? "cal-month-empty" : "",
              day && isSameDay(day, today) ? "cal-month-today" : "",
            ].join(" ")}
            onClick={() => day && onDayClick(day)}
          >
            {day && (
              <>
                <span className="cal-month-day-num">{day.getDate()}</span>
                {apptCountForDay(day) > 0 && (
                  <div className="cal-month-appts">
                    {apptsForDay(day).slice(0, 3).map((a) => (
                      <div key={a.id} className={`cal-month-appt-chip cal-chip-${a.color}`}>
                        {a.startTime} {a.service}
                      </div>
                    ))}
                    {apptCountForDay(day) > 3 && (
                      <div className="cal-month-more">+{apptCountForDay(day) - 3} more</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function NewAppointmentModal({
  visible,
  onClose,
  onCreated,
  barbers,
  defaultDate,
  defaultStaffId,
}: {
  visible: boolean;
  onClose: () => void;
  onCreated: () => void;
  barbers: Barber[];
  defaultDate?: Date;
  defaultStaffId?: string;
}) {
  const [form] = Form.useForm();
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<RealService[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    axiosClient.get("/clients").then((r) => setClients(r.data ?? [])).catch(() => {});
    axiosClient.get("/settings/services").then((r) => setServices(r.data ?? [])).catch(() => setServices([]));
    form.setFieldsValue({
      date: dayjs(defaultDate ?? new Date()),
      staff_id: defaultStaffId ?? undefined,
    });
  }, [visible, defaultDate, defaultStaffId, form]);

  const svc = services.find((s) => s.name === selectedService);

  async function handleOk() {
    let vals: any;
    try {
      vals = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const svcObj = services.find((s) => s.name === vals.service);
      const startDayjs: Dayjs = vals.date
        .hour(vals.start_time.hour())
        .minute(vals.start_time.minute())
        .second(0);
      const endDayjs = startDayjs.add(svcObj?.duration_min ?? 30, "minute");

      await axiosClient.post("/bookings", {
        client_id: vals.client_id,
        staff_id: vals.staff_id,
        service: vals.service,
        service_id: svcObj?.service_id,
        start_time: startDayjs.toISOString(),
        end_time: endDayjs.toISOString(),
        price: svcObj?.price ?? vals.price ?? 0,
      });

      message.success("Appointment created");
      form.resetFields();
      setSelectedService(null);
      onCreated();
      onClose();
    } catch (err: any) {
      message.error(err?.response?.data?.error ?? "Failed to create appointment");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="New Appointment"
      open={visible}
      onCancel={() => { form.resetFields(); setSelectedService(null); onClose(); }}
      onOk={handleOk}
      okText="Book"
      confirmLoading={submitting}
      width={480}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="client_id" label="Client" rules={[{ required: true, message: "Pick a client" }]}>
          <Select
            showSearch
            placeholder="Search clients..."
            filterOption={(input, opt) =>
              (opt?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={clients.map((c) => ({ label: c.name, value: c.client_id }))}
          />
        </Form.Item>

        <Form.Item name="service" label="Service" rules={[{ required: true, message: "Pick a service" }]}>
          <Select
            placeholder="Select service"
            onChange={(val) => setSelectedService(val)}
            options={services.map((s) => ({
              label: `${s.name} (${s.duration_min} min · $${s.price})`,
              value: s.name,
            }))}
          />
        </Form.Item>

        <Form.Item name="staff_id" label="Staff Member" rules={[{ required: true, message: "Pick a staff member" }]}>
          <Select
            placeholder="Select staff"
            options={barbers.map((b) => ({ label: b.name, value: b.user_id }))}
          />
        </Form.Item>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="start_time" label="Start Time" rules={[{ required: true }]}>
            <TimePicker style={{ width: "100%" }} format="h:mm A" minuteStep={15} use12Hours />
          </Form.Item>
        </div>

        {svc && (
          <div className="appt-modal-duration-hint">
            Ends ~{svc.duration_min} min after start · Total: <strong>${svc.price}</strong>
          </div>
        )}

        {!svc && (
          <Form.Item name="price" label="Price ($)">
            <InputNumber style={{ width: "100%" }} min={0} precision={2} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

const DATE_RANGE_ROLES = ["admin", "front_desk", "booth_renter"];

function BlockTimeModal({
  visible,
  onClose,
  onCreated,
  barbers,
  defaultDate,
  userRole,
}: {
  visible: boolean;
  onClose: () => void;
  onCreated: () => void;
  barbers: Barber[];
  defaultDate?: Date;
  userRole: string;
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [allStaff, setAllStaff] = useState(false);
  const [rangeMode, setRangeMode] = useState(false);
  const [allDay, setAllDay] = useState(false);
  const canUseRange = DATE_RANGE_ROLES.includes(userRole);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({ date: dayjs(defaultDate ?? new Date()) });
      setAllStaff(false);
      setRangeMode(false);
      setAllDay(false);
    }
  }, [visible, defaultDate, form]);

  async function handleOk() {
    let vals: any;
    try {
      vals = await form.validateFields();
    } catch {
      return;
    }
    const dates: Dayjs[] = rangeMode
      ? (() => {
          const start = vals.date as Dayjs;
          const end = vals.date_end as Dayjs;
          const out: Dayjs[] = [];
          for (let d = start; !d.isAfter(end, "day"); d = d.add(1, "day")) out.push(d);
          return out;
        })()
      : [vals.date as Dayjs];
    const staffIds = allStaff ? barbers.map((b) => b.user_id) : [vals.staff_id];
    const startTime = allDay ? "00:00" : vals.start_time.format("HH:mm");
    const endTime = allDay ? "23:59" : vals.end_time.format("HH:mm");

    setSubmitting(true);
    try {
      await Promise.all(
        staffIds.flatMap((staffId) =>
          dates.map((d) =>
            axiosClient.post("/calendar/time-blocks", {
              staff_id: staffId,
              block_date: d.format("YYYY-MM-DD"),
              start_time: startTime,
              end_time: endTime,
              reason: vals.reason ?? "",
            })
          )
        )
      );
      message.success(dates.length > 1 || staffIds.length > 1 ? "Time blocked" : "Time blocked");
      form.resetFields();
      onCreated();
      onClose();
    } catch (err: any) {
      message.error(err?.response?.data ?? "Failed to block time");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Block Time"
      open={visible}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={handleOk}
      okText="Block"
      confirmLoading={submitting}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item label="Staff Member">
          <Checkbox checked={allStaff} onChange={(e: CheckboxChangeEvent) => setAllStaff(e.target.checked)} style={{ marginBottom: 8 }}>
            All Staff
          </Checkbox>
          {!allStaff && (
            <Form.Item name="staff_id" rules={[{ required: true, message: "Select a staff member" }]} noStyle>
              <Select
                placeholder="Select staff"
                options={barbers.map((b) => ({ label: b.name, value: b.user_id }))}
              />
            </Form.Item>
          )}
        </Form.Item>

        {canUseRange && (
          <Form.Item label="Date Range">
            <Checkbox checked={rangeMode} onChange={(e: CheckboxChangeEvent) => setRangeMode(e.target.checked)}>
              Block a range of days
            </Checkbox>
          </Form.Item>
        )}

        <div style={{ display: "grid", gridTemplateColumns: rangeMode ? "1fr 1fr" : "1fr", gap: 12 }}>
          <Form.Item name="date" label={rangeMode ? "From" : "Date"} rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          {rangeMode && (
            <Form.Item name="date_end" label="To" rules={[{ required: true }]}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          )}
        </div>

        <Form.Item label="All Day">
          <Checkbox checked={allDay} onChange={(e: CheckboxChangeEvent) => setAllDay(e.target.checked)}>
            Block the entire day
          </Checkbox>
        </Form.Item>

        {!allDay && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Item name="start_time" label="Start Time" rules={[{ required: true }]}>
              <TimePicker style={{ width: "100%" }} format="h:mm A" minuteStep={15} use12Hours />
            </Form.Item>
            <Form.Item name="end_time" label="End Time" rules={[{ required: true }]}>
              <TimePicker style={{ width: "100%" }} format="h:mm A" minuteStep={15} use12Hours />
            </Form.Item>
          </div>
        )}
        <Form.Item name="reason" label="Reason (optional)">
          <Input placeholder="e.g. Lunch break, Training..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Quick "New Client" modal ────────────────────────────────────────────────
function AddClientModal({
  visible,
  onClose,
  onCreated,
}: {
  visible: boolean;
  onClose: () => void;
  onCreated: (clientId: string, name: string) => void;
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) form.resetFields();
  }, [visible, form]);

  async function handleOk() {
    let vals: any;
    try {
      vals = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const res = await axiosClient.post("/clients", {
        name: vals.name,
        email: vals.email || undefined,
        phone: vals.phone || undefined,
        address: vals.address || undefined,
        notes: vals.notes || undefined,
      });
      message.success(`${vals.name} added`);
      form.resetFields();
      onCreated(res.data.client_id, vals.name);
      onClose();
    } catch (err: any) {
      message.error(err?.response?.data ?? "Failed to add client");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="New Client"
      open={visible}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={handleOk}
      okText="Add Client"
      confirmLoading={submitting}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="name" label="Full Name" rules={[{ required: true, message: "Name is required" }]}>
          <Input placeholder="e.g. Jane Smith" autoFocus />
        </Form.Item>
        <Form.Item name="email" label="Email" rules={[{ type: "email", message: "Enter a valid email" }]}>
          <Input placeholder="jane@example.com" />
        </Form.Item>
        <Form.Item name="phone" label="Phone">
          <Input placeholder="(555) 000-0000" />
        </Form.Item>
        <Form.Item name="address" label="Address">
          <Input placeholder="123 Main St, City, ST 00000" />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={3} placeholder="Allergies, preferences, reminders…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Waitlist: click-to-book modal (the non-drag-and-drop path) ────────────────
function WaitlistBookModal({
  entry,
  barbers,
  onClose,
  onBooked,
}: {
  entry: WaitlistEntry | null;
  barbers: Barber[];
  onClose: () => void;
  onBooked: () => void;
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (entry) {
      form.resetFields();
      form.setFieldsValue({ date: dayjs() });
    }
  }, [entry, form]);

  async function handleOk() {
    if (!entry) return;
    let vals: any;
    try {
      vals = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const startDayjs: Dayjs = vals.date
        .hour(vals.start_time.hour())
        .minute(vals.start_time.minute())
        .second(0);
      const endDayjs = startDayjs.add(60, "minute");
      await axiosClient.post(`/waitlist/${entry.waitlist_id}/book`, {
        staff_id: vals.staff_id,
        start_time: startDayjs.toISOString(),
        end_time: endDayjs.toISOString(),
        price: 0,
      });
      message.success(`${entry.client_name} booked!`);
      form.resetFields();
      onBooked();
      onClose();
    } catch (err: any) {
      message.error(err?.response?.data ?? "That time is no longer available.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={!!entry}
      title={entry ? `Book ${entry.client_name}` : "Book"}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={handleOk}
      okText="Book"
      confirmLoading={submitting}
    >
      {entry && (
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Text type="secondary">Service: {entry.service}</Text>
          <Form.Item name="staff_id" label="Staff Member" rules={[{ required: true, message: "Pick a staff member" }]} style={{ marginTop: 12 }}>
            <Select placeholder="Select staff" options={barbers.map((b) => ({ label: b.name, value: b.user_id }))} />
          </Form.Item>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="start_time" label="Start Time" rules={[{ required: true }]}>
              <TimePicker style={{ width: "100%" }} format="h:mm A" minuteStep={15} use12Hours />
            </Form.Item>
          </div>
        </Form>
      )}
    </Modal>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminCalendarPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>("day");
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>(["all"]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [importApptsOpen, setImportApptsOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [newApptOpen, setNewApptOpen] = useState(false);
  const [blockTimeOpen, setBlockTimeOpen] = useState(false);
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [defaultSlotDate, setDefaultSlotDate] = useState<Date | undefined>();
  const [defaultStaffId, setDefaultStaffId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleForm] = Form.useForm();
  const [rescheduleSaving, setRescheduleSaving] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const now = useNowTick();

  const auth = useAppSelector((s) => s.auth);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [addingWaitlist, setAddingWaitlist] = useState(false);
  const [newWlName, setNewWlName] = useState("");
  const [newWlService, setNewWlService] = useState("");
  const [newWlPhone, setNewWlPhone] = useState("");
  const [draggingWaitlistId, setDraggingWaitlistId] = useState<string | null>(null);
  const [draggingAppointmentId, setDraggingAppointmentId] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [bookWlEntry, setBookWlEntry] = useState<WaitlistEntry | null>(null);
  const [timeBlocks, setTimeBlocks] = useState<StaffTimeBlockUI[]>([]);
  const [canChangeWaitlist, setCanChangeWaitlist] = useState(false);
  const [noShowPolicy, setNoShowPolicy] = useState<{ penalty_type: string; penalty_amount: number } | null>(null);
  const [noShowCharged, setNoShowCharged] = useState(false);
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [chargeAmount, setChargeAmount] = useState(0);
  const [charging, setCharging] = useState(false);

  useEffect(() => {
    if (auth.role !== "admin") return;
    axiosClient.get("/settings/no-show-policy").then((r) => setNoShowPolicy(r.data)).catch(() => {});
  }, [auth.role]);

  useEffect(() => {
    if (!selectedAppointment || selectedAppointment.status !== "no_show") {
      setNoShowCharged(false);
      return;
    }
    axiosClient
      .get("/transactions", { params: { booking_id: selectedAppointment.id } })
      .then(() => setNoShowCharged(true))
      .catch(() => setNoShowCharged(false));
  }, [selectedAppointment]);

  function openChargeModal() {
    if (!selectedAppointment) return;
    const suggested = noShowPolicy
      ? noShowPolicy.penalty_type === "percent"
        ? (selectedAppointment.price * noShowPolicy.penalty_amount) / 100
        : noShowPolicy.penalty_amount
      : selectedAppointment.price;
    setChargeAmount(Math.round(suggested * 100) / 100);
    setChargeModalOpen(true);
  }

  async function confirmChargeNoShow() {
    if (!selectedAppointment) return;
    setCharging(true);
    try {
      await axiosClient.post(`/bookings/${selectedAppointment.id}/checkout`, {
        payment_method: "manual_entry",
        amount: chargeAmount,
        client_id: selectedAppointment.clientId || undefined,
        staff_id: selectedAppointment.staffMember,
      });
      message.success("No-show fee charged");
      setNoShowCharged(true);
      setChargeModalOpen(false);
    } catch (err: any) {
      message.error(err?.response?.data ?? "Failed to charge no-show fee");
    } finally {
      setCharging(false);
    }
  }

  function fetchWaitlist() {
    axiosClient.get("/waitlist").then((r) => setWaitlist(r.data ?? [])).catch(() => {});
  }

  useEffect(() => {
    axiosClient.get("/me/permissions")
      .then((r) => setCanChangeWaitlist(!!r.data?.can_change_waitlist))
      .catch(() => {});
  }, []);

  function fetchTimeBlocks() {
    axiosClient
      .get("/calendar/time-blocks", { params: { from: dayjs(currentDate).format("YYYY-MM-DD"), to: dayjs(currentDate).add(1, "day").format("YYYY-MM-DD") } })
      .then((r) => setTimeBlocks(r.data ?? []))
      .catch(() => {});
  }

  useEffect(() => { fetchWaitlist(); }, []);
  useEffect(() => { fetchTimeBlocks(); }, [currentDate]);

  async function addToWaitlist() {
    if (!newWlName.trim()) return;
    try {
      await axiosClient.post("/waitlist", {
        client_name: newWlName.trim(),
        client_phone: newWlPhone.trim(),
        service: newWlService.trim() || "Walk-in",
      });
      setNewWlName(""); setNewWlService(""); setNewWlPhone(""); setAddingWaitlist(false);
      fetchWaitlist();
    } catch {
      message.error("Failed to add to waitlist.");
    }
  }

  async function removeFromWaitlist(id: string) {
    try {
      await axiosClient.delete(`/waitlist/${id}`);
      setWaitlist((prev) => prev.filter((e) => e.waitlist_id !== id));
    } catch {
      message.error("Failed to remove from waitlist.");
    }
  }

  async function bookWaitlistEntryToSlot(entry: WaitlistEntry, staffId: string, time: string) {
    const start = slotToISO(currentDate, time);
    const end = dayjs(start).add(60, "minute").toISOString();
    try {
      await axiosClient.post(`/waitlist/${entry.waitlist_id}/book`, {
        staff_id: staffId,
        start_time: start,
        end_time: end,
        price: 0,
      });
      message.success(`${entry.client_name} booked!`);
      setWaitlist((prev) => prev.filter((e) => e.waitlist_id !== entry.waitlist_id));
      fetchCalendarData();
    } catch (err: any) {
      message.error(err?.response?.data ?? "That time is no longer available.");
    }
  }

  const permissions = getPermissions(auth.role);

  function isStaffVisible(staffId: string): boolean {
    if (permissions.ownBookingsOnly) return staffId === auth.userId;
    return selectedCalendars.includes("all") || selectedCalendars.includes(staffId);
  }

  const visibleStaff = useMemo(() => {
    const list = permissions.ownBookingsOnly
      ? barbers.filter((b) => b.user_id === auth.userId)
      : selectedCalendars.includes("all")
      ? barbers
      : barbers.filter((b) => selectedCalendars.includes(b.user_id));
    return list.map((b) => ({ id: b.user_id, name: b.name, avatar: b.name.charAt(0).toUpperCase(), avatarUrl: b.avatar_url || undefined }));
  }, [selectedCalendars, barbers, permissions.ownBookingsOnly, auth.userId]);

  const appointmentsForCurrentDate = useMemo(
    () => appointments.filter((a) => isSameDay(new Date(a.startAt), currentDate)),
    [appointments, currentDate]
  );

  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);

  async function fetchCalendarData() {
    setIsLoading(true);
    setFetchError(null);
    try {
      const { from, to } = getFetchRange(currentDate, viewMode);

      const [bookingsRes, barbersRes] = await Promise.all([
        axiosClient.get("/calendar/bookings", { params: { from, to } }),
        axiosClient.get("/calendar/barbers"),
      ]);

      const bookingData = (bookingsRes.data ?? []) as Array<{
        booking_id: string;
        client_id: string;
        client_name: string;
        service: string;
        staff_id: string;
        start_time: string;
        end_time: string;
        price?: number;
        status?: string;
      }>;

      setAppointments(
        bookingData.map((b) => ({
          id: b.booking_id,
          clientId: b.client_id,
          clientName: b.client_name,
          service: b.service,
          staffMember: b.staff_id,
          startAt: b.start_time,
          endAt: b.end_time,
          startTime: formatTime(b.start_time),
          endTime: formatTime(b.end_time),
          price: b.price ?? 0,
          color: appointmentColorForService(b.service),
          status: b.status ?? "scheduled",
        }))
      );

      setBarbers(barbersRes.data ?? []);
    } catch (err: any) {
      setFetchError(err?.response?.data?.error ?? "Unable to load calendar data.");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, viewMode]);

  const viewModeItems: MenuProps["items"] = [
    { key: "day", label: "Day View", onClick: () => setViewMode("day") },
    { key: "week", label: "Week View", onClick: () => setViewMode("week") },
    { key: "month", label: "Month View", onClick: () => setViewMode("month") },
  ];

  function goPrev() {
    const d = new Date(currentDate);
    if (viewMode === "day") d.setDate(d.getDate() - 1);
    else if (viewMode === "week") d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  }

  function goNext() {
    const d = new Date(currentDate);
    if (viewMode === "day") d.setDate(d.getDate() + 1);
    else if (viewMode === "week") d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  }

  function getAppointmentForSlot(staffId: string, time: string): Appointment | undefined {
    return appointmentsForCurrentDate.find(
      (a) => a.staffMember === staffId && a.startTime === time
    );
  }

  function getBlockForSlot(staffId: string, time: string): StaffTimeBlockUI | undefined {
    const dateStr = dayjs(currentDate).format("YYYY-MM-DD");
    const { hour, minute } = parseTimeSlot(time);
    const slotMinutes = hour * 60 + minute;
    return timeBlocks.find((b) => {
      if (b.staff_id !== staffId || b.block_date !== dateStr) return false;
      const [sh, sm] = b.start_time.split(":").map(Number);
      const [eh, em] = b.end_time.split(":").map(Number);
      const startMinutes = sh * 60 + sm;
      const endMinutes = eh * 60 + em;
      return slotMinutes >= startMinutes && slotMinutes < endMinutes;
    });
  }

  function openNewAppt(date?: Date, staffId?: string) {
    setDefaultSlotDate(date ?? currentDate);
    setDefaultStaffId(staffId);
    setQuickActionsOpen(false);
    setNewApptOpen(true);
  }

  function handleCheckout(appt: Appointment) {
    dispatch(resetFlow());
    dispatch(fetchTaxRate());
    dispatch(
      setCheckoutContext({
        bookingId: appt.id,
        clientId: appt.clientId || null,
        clientName: appt.clientName,
        service: appt.service,
        staffId: appt.staffMember,
        amountDue: appt.price,
      })
    );
    navigate("/pos/payment-method");
  }

  async function handleStatusChange(appt: Appointment, status: "in_progress" | "cancelled" | "no_show") {
    setStatusUpdating(true);
    const result = await dispatch(updateBookingStatus({ bookingId: appt.id, status }));
    setStatusUpdating(false);
    if (updateBookingStatus.fulfilled.match(result)) {
      setSelectedAppointment(null);
      fetchCalendarData();
    } else {
      message.error("Failed to update appointment status");
    }
  }

  function openReschedule(appt: Appointment) {
    const start = dayjs(appt.startAt);
    rescheduleForm.setFieldsValue({ date: start, time: start });
    setRescheduleOpen(true);
  }

  async function handleRescheduleSubmit(values: { date: Dayjs; time: Dayjs }) {
    if (!selectedAppointment) return;
    const durationMins = dayjs(selectedAppointment.endAt).diff(dayjs(selectedAppointment.startAt), "minute");
    const newStart = values.date.hour(values.time.hour()).minute(values.time.minute()).second(0);
    const newEnd = newStart.add(durationMins, "minute");

    setRescheduleSaving(true);
    const result = await dispatch(
      rescheduleBooking({
        bookingId: selectedAppointment.id,
        startTime: newStart.toISOString(),
        endTime: newEnd.toISOString(),
      })
    );
    setRescheduleSaving(false);

    if (rescheduleBooking.fulfilled.match(result)) {
      message.success("Appointment rescheduled");
      setRescheduleOpen(false);
      setSelectedAppointment(null);
      fetchCalendarData();
    } else {
      message.error("Failed to reschedule appointment");
    }
  }

  return (
    <Layout className="admin-calendar-page">

      <Content className="calendar-content">
        <div className="calendar-controls">
          <Space>
            <Button icon={<ChevronLeft size={18} />} onClick={goPrev} />
            <Button onClick={() => setCurrentDate(new Date())}>TODAY</Button>
            <Button icon={<ChevronRight size={18} />} onClick={goNext} />
            <Text className="calendar-current-date">
              {formatDateLabel(currentDate, viewMode)}
            </Text>
          </Space>

          <Space>
            <Dropdown menu={{ items: viewModeItems }} trigger={["click"]}>
              <Button>
                {viewMode === "day" ? "Day View" : viewMode === "week" ? "Week View" : "Month View"}
              </Button>
            </Dropdown>

            {!permissions.ownBookingsOnly && (
              <Select
                mode="multiple"
                value={selectedCalendars}
                className="calendar-select"
                onChange={(vals) => {
                  if (vals.includes("all")) setSelectedCalendars(["all"]);
                  else setSelectedCalendars(vals);
                }}
                options={[
                  { value: "all", label: "All calendars" },
                  ...barbers.map((b) => ({ value: b.user_id, label: b.name })),
                ]}
              />
            )}

            {canChangeWaitlist && (
              <Button
                icon={<Users size={18} />}
                onClick={() => setWaitlistOpen((v) => !v)}
              >
                Waitlist{waitlist.length > 0 ? ` (${waitlist.length})` : ""}
              </Button>
            )}

            <Button
              type="primary"
              icon={<PlusCircle size={18} />}
              onClick={() => setQuickActionsOpen(true)}
            />
          </Space>
        </div>

        {isLoading && <div className="calendar-loading">Loading...</div>}

        {fetchError && (
          <div className="calendar-error-message">
            <Text type="danger">{fetchError}</Text>
          </div>
        )}

        {/* ── Day view ── */}
        {viewMode === "day" && (
          <div
            className="calendar-day-grid"
            style={{
              gridTemplateColumns: `80px repeat(${visibleStaff.length}, minmax(220px, 1fr))`,
            }}
          >
            {isSameDay(currentDate, new Date()) && (() => {
              const nowSlot = nowLineSlot(now);
              if (!nowSlot) return null;
              return (
                <>
                  <NowTimeLabel now={now} gridRow={nowSlot.rowIndex + 2} />
                  <NowTimeBar now={now} gridRow={nowSlot.rowIndex + 2} gridColumn="2 / -1" />
                </>
              );
            })()}

            <div className="calendar-header-cell" />

            {visibleStaff.map((staff) => {
              const count = appointmentsForCurrentDate.filter(
                (a) => a.staffMember === staff.id
              ).length;
              return (
                <div key={staff.id} className="calendar-staff-header">
                  <Avatar src={staff.avatarUrl}>{staff.avatar}</Avatar>
                  <div>
                    <Text strong>{staff.name}</Text>
                    <div className="calendar-staff-count">{count} bookings</div>
                  </div>
                </div>
              );
            })}

            {TIME_SLOTS.map((time) => (
              <>
                <div key={`${time}-lbl`} className="calendar-time-cell">{time}</div>
                {visibleStaff.map((staff) => {
                  const appt = getAppointmentForSlot(staff.id, time);
                  const block = !appt ? getBlockForSlot(staff.id, time) : undefined;
                  const slotKey = `${staff.id}-${time}`;
                  const isDropTarget = !appt && !block && (draggingWaitlistId || draggingAppointmentId);
                  const isReschedulable = appt && !["cancelled", "completed", "no_show"].includes(appt.status);
                  return (
                    <div
                      key={slotKey}
                      className="calendar-slot-cell"
                      onClick={() => !appt && !block && openNewAppt(currentDate, staff.id)}
                      onDragOver={(e) => { if (isDropTarget) { e.preventDefault(); setDragOverKey(slotKey); } }}
                      onDragLeave={() => setDragOverKey((k) => (k === slotKey ? null : k))}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOverKey(null);
                        if (!isDropTarget) return;
                        if (draggingAppointmentId) {
                          const dragged = appointments.find((a) => a.id === draggingAppointmentId);
                          setDraggingAppointmentId(null);
                          if (!dragged) return;
                          const durationMins = dayjs(dragged.endAt).diff(dayjs(dragged.startAt), "minute");
                          const newStart = slotToISO(currentDate, time);
                          const newEnd = dayjs(newStart).add(durationMins, "minute").toISOString();
                          dispatch(rescheduleBooking({ bookingId: dragged.id, startTime: newStart, endTime: newEnd })).then((result) => {
                            if (rescheduleBooking.fulfilled.match(result)) {
                              message.success("Appointment rescheduled");
                              fetchCalendarData();
                            } else {
                              message.error("Failed to reschedule appointment");
                            }
                          });
                          return;
                        }
                        if (!draggingWaitlistId) return;
                        const entry = waitlist.find((w) => w.waitlist_id === draggingWaitlistId);
                        if (entry) bookWaitlistEntryToSlot(entry, staff.id, time);
                        setDraggingWaitlistId(null);
                      }}
                      style={
                        isDropTarget && dragOverKey === slotKey
                          ? { background: "#dcfce7", outline: "2px solid #22c55e", outlineOffset: -2 }
                          : block
                          ? { background: "#f3f4f6", cursor: "not-allowed" }
                          : undefined
                      }
                    >
                      {appt && (
                        <div
                          className={appointmentClass(appt.color)}
                          draggable={isReschedulable}
                          style={{
                            padding: "8px 10px",
                            borderRadius: 10,
                            cursor: isReschedulable ? "grab" : "pointer",
                            height: "100%",
                            opacity: draggingAppointmentId === appt.id ? 0.4 : 1,
                          }}
                          onClick={(e) => { e.stopPropagation(); setSelectedAppointment(appt); }}
                          onDragStart={(e) => { e.stopPropagation(); setDraggingAppointmentId(appt.id); }}
                          onDragEnd={() => { setDraggingAppointmentId(null); setDragOverKey(null); }}
                        >
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{appt.service}</div>
                          <div style={{ fontSize: 12 }}>{appt.clientName}</div>
                          <div style={{ fontSize: 11, opacity: 0.75 }}>{appt.startTime} – {appt.endTime}</div>
                        </div>
                      )}
                      {block && (
                        <div style={{ padding: "8px 10px", height: "100%", color: "#9ca3af", fontSize: 12 }}>
                          <div style={{ fontWeight: 600 }}>Blocked</div>
                          {block.reason && <div style={{ fontSize: 11 }}>{block.reason}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            ))}
          </div>
        )}

        {/* ── Week view ── */}
        {viewMode === "week" && (
          <WeekView
            weekDays={weekDays}
            appointments={appointments.filter((a) => isStaffVisible(a.staffMember))}
            onAppointmentClick={setSelectedAppointment}
            onSlotClick={(day, _time) => openNewAppt(day)}
          />
        )}

        {/* ── Month view ── */}
        {viewMode === "month" && (
          <MonthView
            currentDate={currentDate}
            appointments={appointments.filter((a) => isStaffVisible(a.staffMember))}
            onDayClick={(day) => {
              setCurrentDate(day);
              setViewMode("day");
            }}
          />
        )}
      </Content>

      {/* ── Appointment details modal ── */}
      <Modal
        open={!!selectedAppointment}
        title="Appointment Details"
        onCancel={() => setSelectedAppointment(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedAppointment(null)}>
            Close
          </Button>,
          ...(selectedAppointment?.status === "cancelled" || selectedAppointment?.status === "completed" || selectedAppointment?.status === "no_show"
            ? []
            : [
                <Button
                  key="cancel"
                  danger
                  loading={statusUpdating}
                  onClick={() => { if (selectedAppointment) handleStatusChange(selectedAppointment, "cancelled"); }}
                >
                  Cancel Appointment
                </Button>,
                <Button
                  key="reschedule"
                  onClick={() => { if (selectedAppointment) openReschedule(selectedAppointment); }}
                >
                  Reschedule
                </Button>,
              ]),
          ...(selectedAppointment?.status === "scheduled"
            ? [
                <Button
                  key="checkin"
                  loading={statusUpdating}
                  onClick={() => { if (selectedAppointment) handleStatusChange(selectedAppointment, "in_progress"); }}
                >
                  Check In
                </Button>,
              ]
            : []),
          ...((selectedAppointment?.status === "scheduled" || selectedAppointment?.status === "in_progress") && DATE_RANGE_ROLES.includes(auth.role ?? "")
            ? [
                <Button
                  key="noshow"
                  loading={statusUpdating}
                  onClick={() => { if (selectedAppointment) handleStatusChange(selectedAppointment, "no_show"); }}
                >
                  Mark No-Show
                </Button>,
              ]
            : []),
          ...(selectedAppointment?.status === "no_show" && DATE_RANGE_ROLES.includes(auth.role ?? "")
            ? [
                noShowCharged ? (
                  <Tag key="charged" color="green" style={{ marginLeft: 8 }}>Fee Charged</Tag>
                ) : (
                  <Button key="chargeNoShow" danger onClick={openChargeModal}>
                    Charge No-Show Fee
                  </Button>
                ),
              ]
            : []),
          ...(selectedAppointment?.status === "cancelled" || selectedAppointment?.status === "completed" || selectedAppointment?.status === "no_show"
            ? []
            : [
                <Button
                  key="checkout"
                  type="primary"
                  onClick={() => { if (selectedAppointment) handleCheckout(selectedAppointment); }}
                >
                  Check Out · ${selectedAppointment?.price?.toFixed(2) ?? "0.00"}
                </Button>,
              ]),
        ]}
      >
        {selectedAppointment && (
          <Space direction="vertical" size={8}>
            <div><Text type="secondary">Service</Text><br /><Text strong>{selectedAppointment.service}</Text></div>
            <div><Text type="secondary">Client</Text><br /><Text>{selectedAppointment.clientName}</Text></div>
            <div><Text type="secondary">Time</Text><br /><Text>{selectedAppointment.startTime} – {selectedAppointment.endTime}</Text></div>
            <div><Text type="secondary">Price</Text><br /><Text strong>${selectedAppointment.price.toFixed(2)}</Text></div>
            <div><Text type="secondary">Status</Text><br /><Text strong style={{ textTransform: "capitalize" }}>{selectedAppointment.status.replace("_", " ")}</Text></div>
          </Space>
        )}
      </Modal>

      {/* ── Charge no-show fee modal ── */}
      <Modal
        title="Charge No-Show Fee"
        open={chargeModalOpen}
        onCancel={() => setChargeModalOpen(false)}
        onOk={confirmChargeNoShow}
        okText="Charge"
        okButtonProps={{ danger: true, loading: charging }}
      >
        <Space direction="vertical" style={{ width: "100%", marginTop: 16 }}>
          <Text type="secondary">
            {noShowPolicy
              ? `Suggested from your no-show policy (${noShowPolicy.penalty_type === "percent" ? `${noShowPolicy.penalty_amount}% of price` : `$${noShowPolicy.penalty_amount} flat`}). Adjust if needed.`
              : "Enter the fee amount to charge."}
          </Text>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            precision={2}
            prefix="$"
            value={chargeAmount}
            onChange={(v) => setChargeAmount(v ?? 0)}
          />
        </Space>
      </Modal>

      {/* ── Reschedule modal ── */}
      <Modal
        open={rescheduleOpen}
        title="Reschedule Appointment"
        onCancel={() => setRescheduleOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={rescheduleForm} layout="vertical" onFinish={handleRescheduleSubmit} style={{ marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Form.Item name="date" label="Date" rules={[{ required: true, message: "Pick a date" }]}>
              <DatePicker style={{ width: "100%" }} size="large" />
            </Form.Item>
            <Form.Item name="time" label="Start Time" rules={[{ required: true, message: "Pick a start time" }]}>
              <TimePicker style={{ width: "100%" }} size="large" format="h:mm A" minuteStep={15} use12Hours />
            </Form.Item>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
            <Button onClick={() => setRescheduleOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={rescheduleSaving}>
              Save New Time
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ── Quick actions modal ── */}
      <Modal
        open={quickActionsOpen}
        title="Quick Actions"
        footer={null}
        onCancel={() => setQuickActionsOpen(false)}
      >
        <Space direction="vertical" className="quick-actions">
          <Button
            type="primary"
            block
            icon={<PlusCircle size={18} />}
            onClick={() => openNewAppt()}
          >
            New Appointment
          </Button>
          <Button
            block
            icon={<Clock size={18} />}
            onClick={() => { setQuickActionsOpen(false); setBlockTimeOpen(true); }}
          >
            Block Time
          </Button>
          <Button
            block
            icon={<DollarSign size={18} />}
            onClick={() => { setQuickActionsOpen(false); navigate("/admin/sales"); }}
          >
            Check Out
          </Button>
          <Button
            block
            icon={<UserPlus size={18} />}
            onClick={() => { setQuickActionsOpen(false); setAddClientOpen(true); }}
          >
            New Client
          </Button>
          {!permissions.ownBookingsOnly && (
            <Button
              block
              icon={<Upload size={18} />}
              onClick={() => { setQuickActionsOpen(false); setImportApptsOpen(true); }}
            >
              Import Appointments (CSV)
            </Button>
          )}
        </Space>
      </Modal>

      {importApptsOpen && (
        <ImportAppointmentsModal
          staff={barbers}
          onClose={() => setImportApptsOpen(false)}
          onImported={fetchCalendarData}
        />
      )}

      {/* ── New appointment modal ── */}
      <NewAppointmentModal
        visible={newApptOpen}
        onClose={() => setNewApptOpen(false)}
        onCreated={fetchCalendarData}
        barbers={barbers}
        defaultDate={defaultSlotDate}
        defaultStaffId={defaultStaffId}
      />

      {/* ── Block time modal ── */}
      <BlockTimeModal
        visible={blockTimeOpen}
        onClose={() => setBlockTimeOpen(false)}
        onCreated={fetchTimeBlocks}
        barbers={barbers}
        defaultDate={currentDate}
        userRole={auth.role ?? ""}
      />

      {/* ── New client modal ── */}
      <AddClientModal
        visible={addClientOpen}
        onClose={() => setAddClientOpen(false)}
        onCreated={() => {}}
      />

      {/* ── Waitlist: click-to-book modal ── */}
      <WaitlistBookModal
        entry={bookWlEntry}
        barbers={barbers}
        onClose={() => setBookWlEntry(null)}
        onBooked={() => { fetchWaitlist(); fetchCalendarData(); }}
      />

      {/* ── Waitlist side panel ── */}
      {waitlistOpen && (
        <div
          style={{
            position: "fixed", top: 0, right: 0, bottom: 0, width: 300,
            background: "#fff", borderLeft: "1px solid #e5e7eb", boxShadow: "-2px 0 8px rgba(0,0,0,0.06)",
            zIndex: 1000, display: "flex", flexDirection: "column",
          }}
        >
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Text strong>Waitlist{waitlist.length > 0 ? ` (${waitlist.length})` : ""}</Text>
            <Button type="text" size="small" icon={<X size={16} />} onClick={() => setWaitlistOpen(false)} />
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
            {waitlist.length === 0 && (
              <Text type="secondary" style={{ fontSize: 13 }}>Nobody on the waitlist.</Text>
            )}
            {waitlist.map((entry, idx) => (
              <div
                key={entry.waitlist_id}
                draggable
                onDragStart={(e) => { setDraggingWaitlistId(entry.waitlist_id); e.dataTransfer.effectAllowed = "move"; }}
                onDragEnd={() => { setDraggingWaitlistId(null); setDragOverKey(null); }}
                style={{
                  border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, marginBottom: 8,
                  cursor: "grab", opacity: draggingWaitlistId === entry.waitlist_id ? 0.4 : 1,
                  background: "#fff",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{
                    width: 18, height: 18, borderRadius: "50%", background: "#fef3c7", color: "#b45309",
                    fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1,
                  }}>
                    {idx + 1}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{entry.client_name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{entry.service}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      {entry.client_phone || "—"} · {dayjs(entry.created_at).format("h:mm A")}
                    </div>
                  </div>
                  <button
                    onClick={() => removeFromWaitlist(entry.waitlist_id)}
                    title="Remove"
                    style={{ border: "none", background: "transparent", cursor: "pointer", color: "#ef4444", padding: 2 }}
                  >
                    <X size={14} />
                  </button>
                </div>
                <Button size="small" block style={{ marginTop: 8 }} onClick={() => setBookWlEntry(entry)}>
                  Book…
                </Button>
              </div>
            ))}
            {draggingWaitlistId && (
              <Text type="secondary" style={{ fontSize: 11, fontStyle: "italic" }}>Drop on an open slot in Day View →</Text>
            )}
          </div>

          <div style={{ padding: 12, borderTop: "1px solid #e5e7eb", background: "#fafafa" }}>
            {!addingWaitlist ? (
              <Button type="primary" block icon={<PlusCircle size={14} />} onClick={() => setAddingWaitlist(true)}>
                Add to Waitlist
              </Button>
            ) : (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input placeholder="Client name *" value={newWlName} onChange={(e) => setNewWlName(e.target.value)} autoFocus />
                <Input placeholder="Service (e.g. Men's Haircut)" value={newWlService} onChange={(e) => setNewWlService(e.target.value)} />
                <Input placeholder="Phone number" value={newWlPhone} onChange={(e) => setNewWlPhone(e.target.value)} />
                <Space style={{ width: "100%" }}>
                  <Button type="primary" onClick={addToWaitlist}>Add</Button>
                  <Button onClick={() => { setAddingWaitlist(false); setNewWlName(""); setNewWlService(""); setNewWlPhone(""); }}>Cancel</Button>
                </Space>
              </Space>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
